/**
 * CanvasCompositor - Composites camera and screen share streams via canvas.
 *
 * Runs from the moment streaming starts in camera-only mode (full-frame camera).
 * When screen sharing starts, switches to screen-share mode: screen as fullscreen
 * with letterboxing/pillarboxing, camera as a PiP overlay in the bottom-right corner.
 */

type CompositorState = "idle" | "running" | "stopped";
type CompositorMode = "camera-only" | "screen-share";
export type CompositorOrientation = "landscape" | "portrait";

export class CanvasCompositor {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private outputStream: MediaStream;

  private screenVideo: HTMLVideoElement | null = null;
  private cameraVideo: HTMLVideoElement | null = null;

  private timerWorker: Worker | null = null;
  private timerWorkerUrl: string | null = null;
  private state: CompositorState = "idle";
  private mode: CompositorMode = "camera-only";
  private readyPromise: Promise<void> | null = null;
  private orientation: CompositorOrientation = "landscape";

  private readonly FRAME_RATE = 24;

  private get OUTPUT_WIDTH(): number {
    return this.orientation === "landscape" ? 1280 : 720;
  }

  private get OUTPUT_HEIGHT(): number {
    return this.orientation === "landscape" ? 720 : 1280;
  }

  private readonly PIP_WIDTH_RATIO = 0.25;
  private readonly PIP_PADDING = 16;
  private readonly PIP_BORDER_RADIUS = 8;
  private readonly PIP_BORDER_WIDTH = 2;

  constructor(orientation: CompositorOrientation = "landscape") {
    this.orientation = orientation;
    this.canvas = document.createElement("canvas");
    this.canvas.width = this.OUTPUT_WIDTH;
    this.canvas.height = this.OUTPUT_HEIGHT;

    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get 2D context from canvas");
    }
    this.ctx = ctx;

    this.outputStream = this.canvas.captureStream(this.FRAME_RATE);
  }

  /**
   * Start compositing in camera-only mode (full-frame camera).
   */
  startCameraOnly(cameraStream: MediaStream): void {
    if (this.state === "running") {
      console.warn("Compositor already running");
      return;
    }

    this.state = "running";
    this.mode = "camera-only";
    this.cameraVideo = this.createVideoElement(cameraStream);
    this.readyPromise = this.createReadyPromise();
    this.renderLoop();
  }

  /**
   * Add a screen share stream — switches to screen-share mode with camera PiP.
   */
  addScreenStream(screenStream: MediaStream): void {
    this.screenVideo = this.createVideoElement(screenStream);
    this.mode = "screen-share";

    // Ensure camera video is still decoding after adding second video element
    if (this.cameraVideo && this.cameraVideo.paused) {
      this.cameraVideo.play().catch((err) => console.warn("Camera re-play failed:", err));
    }
  }

  /**
   * Switch between landscape (1280x720) and portrait (720x1280) output.
   */
  setOrientation(orientation: CompositorOrientation): void {
    this.orientation = orientation;
    this.canvas.width = this.OUTPUT_WIDTH;
    this.canvas.height = this.OUTPUT_HEIGHT;
  }

  getOrientation(): CompositorOrientation {
    return this.orientation;
  }

  /**
   * Replace the camera stream (e.g. when switching front/rear camera).
   */
  updateCameraStream(cameraStream: MediaStream): void {
    if (this.cameraVideo) {
      this.cameraVideo.srcObject = cameraStream;
      this.cameraVideo.play().catch((err) => console.warn("Camera re-play failed:", err));
    }
  }

  /**
   * Remove the screen share stream — switches back to camera-only mode.
   */
  removeScreenStream(): void {
    if (this.screenVideo) {
      this.screenVideo.srcObject = null;
      this.screenVideo = null;
    }
    this.mode = "camera-only";
  }

  /**
   * Stop the compositor and clean up resources.
   */
  stop(): void {
    this.state = "stopped";

    this.terminateTimerWorker();

    if (this.screenVideo) {
      this.screenVideo.srcObject = null;
      this.screenVideo = null;
    }

    if (this.cameraVideo) {
      this.cameraVideo.srcObject = null;
      this.cameraVideo = null;
    }
  }

  /**
   * Get the composited output stream.
   */
  getOutputStream(): MediaStream {
    return this.outputStream;
  }

  /**
   * Get the video track from the output stream.
   */
  getVideoTrack(): MediaStreamTrack | null {
    const tracks = this.outputStream.getVideoTracks();
    return tracks.length > 0 ? tracks[0]! : null;
  }

  /**
   * Wait until video elements have decoded enough data to render.
   * Uses polling as a fallback since Chrome may skip decode for offscreen elements.
   * Falls back after 3 seconds to prevent indefinite blocking.
   */
  waitUntilReady(): Promise<void> {
    return this.readyPromise ?? Promise.resolve();
  }

  /**
   * Pause the render loop (saves CPU while stream is paused).
   */
  pauseRendering(): void {
    if (this.timerWorker) {
      this.timerWorker.postMessage("stop");
    }
  }

  /**
   * Resume the render loop after pausing.
   */
  resumeRendering(): void {
    if (this.state === "running" && this.timerWorker) {
      this.timerWorker.postMessage("start");
    }
  }

  private createReadyPromise(): Promise<void> {
    const TIMEOUT_MS = 3000;
    const POLL_INTERVAL_MS = 50;

    const waitForVideo = (video: HTMLVideoElement): Promise<void> => {
      if (video.readyState >= 1) return Promise.resolve();

      return new Promise<void>((resolve) => {
        const onLoaded = () => {
          video.removeEventListener("loadeddata", onLoaded);
          clearInterval(pollId);
          resolve();
        };
        video.addEventListener("loadeddata", onLoaded);

        // Polling fallback — Chrome may skip decode for offscreen elements
        const pollId = setInterval(() => {
          if (video.readyState >= 1) {
            video.removeEventListener("loadeddata", onLoaded);
            clearInterval(pollId);
            resolve();
          }
        }, POLL_INTERVAL_MS);
      });
    };

    const videoPromises: Promise<void>[] = [];
    if (this.cameraVideo) videoPromises.push(waitForVideo(this.cameraVideo));
    if (this.screenVideo) videoPromises.push(waitForVideo(this.screenVideo));

    const allReady = Promise.all(videoPromises).then(() => {});
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, TIMEOUT_MS));

    return Promise.race([allReady, timeout]);
  }

  private createVideoElement(stream: MediaStream): HTMLVideoElement {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.play().catch((err) => console.warn("Video play() failed:", err));
    return video;
  }

  /**
   * Start the render loop using a Web Worker timer.
   * Web Workers are not throttled when the tab is inactive, unlike
   * requestAnimationFrame which is completely paused by browsers for
   * background tabs — causing canvas.captureStream() to freeze the
   * WebRTC stream.
   */
  private renderLoop(): void {
    if (this.state !== "running") {
      return;
    }

    this.terminateTimerWorker();
    this.timerWorker = this.createTimerWorker();
    this.timerWorker.onmessage = () => {
      if (this.state === "running") {
        this.drawFrame();
      }
    };
    this.timerWorker.postMessage("start");
  }

  private createTimerWorker(): Worker {
    const workerCode = `
      let intervalId = null;
      const interval = ${Math.round(1000 / this.FRAME_RATE)};
      self.onmessage = function(e) {
        if (e.data === "start") {
          if (intervalId !== null) clearInterval(intervalId);
          intervalId = setInterval(function() { postMessage("tick"); }, interval);
        } else if (e.data === "stop") {
          if (intervalId !== null) { clearInterval(intervalId); intervalId = null; }
        }
      };
    `;
    const blob = new Blob([workerCode], { type: "application/javascript" });
    this.timerWorkerUrl = URL.createObjectURL(blob);
    return new Worker(this.timerWorkerUrl);
  }

  private terminateTimerWorker(): void {
    if (this.timerWorker) {
      this.timerWorker.terminate();
      this.timerWorker = null;
    }
    if (this.timerWorkerUrl) {
      URL.revokeObjectURL(this.timerWorkerUrl);
      this.timerWorkerUrl = null;
    }
  }

  private drawFrame(): void {
    const ctx = this.ctx;

    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, this.OUTPUT_WIDTH, this.OUTPUT_HEIGHT);

    if (this.mode === "camera-only") {
      if (this.cameraVideo && this.cameraVideo.readyState >= 1) {
        this.drawCameraFullFrame();
      }
    } else {
      if (this.screenVideo && this.screenVideo.readyState >= 2) {
        this.drawScreenShare();
      }
      if (this.cameraVideo && this.cameraVideo.readyState >= 1) {
        this.drawCameraPiP();
      }
    }
  }

  /**
   * Draw camera as full-frame.
   * - Same orientation (both portrait or both landscape): cover strategy — crop to fill canvas.
   * - Cross orientation (camera rotated mid-stream): contain strategy — fit full feed, black bars.
   */
  private drawCameraFullFrame(): void {
    if (!this.cameraVideo) return;

    const video = this.cameraVideo;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    if (videoWidth === 0 || videoHeight === 0) return;

    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = this.OUTPUT_WIDTH / this.OUTPUT_HEIGHT;

    const cameraIsLandscape = videoWidth > videoHeight;
    const canvasIsLandscape = this.OUTPUT_WIDTH > this.OUTPUT_HEIGHT;

    if (cameraIsLandscape !== canvasIsLandscape) {
      // Contain: fit full camera feed, black bars fill remaining space
      let drawWidth: number;
      let drawHeight: number;
      if (videoAspect > canvasAspect) {
        drawWidth = this.OUTPUT_WIDTH;
        drawHeight = this.OUTPUT_WIDTH / videoAspect;
      } else {
        drawHeight = this.OUTPUT_HEIGHT;
        drawWidth = this.OUTPUT_HEIGHT * videoAspect;
      }
      const drawX = (this.OUTPUT_WIDTH - drawWidth) / 2;
      const drawY = (this.OUTPUT_HEIGHT - drawHeight) / 2;
      this.ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
    } else {
      // Cover: crop source to fill canvas (same-orientation)
      let srcX = 0,
        srcY = 0,
        srcW = videoWidth,
        srcH = videoHeight;
      if (videoAspect > canvasAspect) {
        srcW = videoHeight * canvasAspect;
        srcX = (videoWidth - srcW) / 2;
      } else if (videoAspect < canvasAspect) {
        srcH = videoWidth / canvasAspect;
        srcY = (videoHeight - srcH) / 2;
      }
      this.ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, this.OUTPUT_WIDTH, this.OUTPUT_HEIGHT);
    }
  }

  private drawScreenShare(): void {
    if (!this.screenVideo) return;

    const video = this.screenVideo;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) return;

    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = this.OUTPUT_WIDTH / this.OUTPUT_HEIGHT;

    let drawWidth: number;
    let drawHeight: number;
    let drawX: number;
    let drawY: number;

    if (videoAspect > canvasAspect) {
      drawWidth = this.OUTPUT_WIDTH;
      drawHeight = this.OUTPUT_WIDTH / videoAspect;
      drawX = 0;
      drawY = (this.OUTPUT_HEIGHT - drawHeight) / 2;
    } else {
      drawHeight = this.OUTPUT_HEIGHT;
      drawWidth = this.OUTPUT_HEIGHT * videoAspect;
      drawX = (this.OUTPUT_WIDTH - drawWidth) / 2;
      drawY = 0;
    }

    this.ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
  }

  private drawCameraPiP(): void {
    if (!this.cameraVideo) return;

    const video = this.cameraVideo;
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) return;

    const pipWidth = this.OUTPUT_WIDTH * this.PIP_WIDTH_RATIO;
    const videoAspect = videoWidth / videoHeight;
    const pipHeight = pipWidth / videoAspect;

    const pipX = this.OUTPUT_WIDTH - pipWidth - this.PIP_PADDING;
    const pipY = this.OUTPUT_HEIGHT - pipHeight - this.PIP_PADDING;

    const ctx = this.ctx;

    ctx.save();

    this.roundRect(pipX, pipY, pipWidth, pipHeight, this.PIP_BORDER_RADIUS);
    ctx.clip();

    // Mirror camera in PiP for natural self-view
    ctx.translate(pipX + pipWidth, pipY);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, pipWidth, pipHeight);

    ctx.restore();

    ctx.save();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
    ctx.lineWidth = this.PIP_BORDER_WIDTH;
    this.roundRect(pipX, pipY, pipWidth, pipHeight, this.PIP_BORDER_RADIUS);
    ctx.stroke();
    ctx.restore();
  }

  private roundRect(x: number, y: number, width: number, height: number, radius: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }
}
